<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid
      :desktop-default-grid="[2, 20]"
      :tablet-default-grid="[1, 14]"
      :mobile-default-grid="[1, 14]"
    >
      <div class="c-workflow-gmp__inner">
        <JssRichText class="c-workflow-gmp-title" :field="fields?.title" />

        <div v-if="isPlatformWorkflow" class="c-workflow-gmp__platform">
          <div
            v-for="(row, rowIndex) in platformWorkflowRows"
            :key="row.id || row.fields.label?.value"
            class="c-workflow-gmp__platform-row"
            :class="`c-workflow-gmp__platform-row--${getPlatformVariant(rowIndex)}`"
          >
            <div class="c-workflow-gmp__platform-label" v-html="row.fields.label.value"></div>
            <div class="c-workflow-gmp__platform-track">
              <article
                v-for="step in getPlatformSteps(row)"
                :key="step.id || step.fields.text?.value"
                class="c-workflow-gmp__platform-step"
                :style="getPlatformStepStyle(step)"
              >
                <span v-html="step.fields.text.value"></span>
              </article>
            </div>
          </div>
        </div>

        <template v-else>
          <div v-if="timelineItems.length" class="c-workflow-gmp__timeline" :style="timelineStyle">
            <span class="c-workflow-gmp__timeline-line" aria-hidden="true"></span>
            <article
              v-for="item in timelineItems"
              :key="item.id || item.fields.label?.value"
              class="c-workflow-gmp__timeline-item"
            >
              <span class="c-workflow-gmp__stage-label">{{ item.fields.label.value }}</span>
              <img
                v-if="getImageSrc(item.fields.icon)"
                class="c-workflow-gmp__stage-icon"
                :src="getImageSrc(item.fields.icon)"
                :alt="getImageAlt(item.fields.icon, item.fields.label.value)"
                loading="lazy"
              />
              <span class="c-workflow-gmp__dot" :style="getDotStyle(item)"></span>
            </article>
          </div>

          <div v-if="gradeStages.length" class="c-workflow-gmp__grades" :style="timelineStyle">
            <article
              v-for="stage in gradeStages"
              :key="stage.id || stage.fields.text?.value"
              class="c-workflow-gmp__grade"
              :style="getGradeStyle(stage)"
              v-html="stage.fields.text.value"
            ></article>
          </div>
        </template>
        <div
          v-if="hasSystemContent"
          class="c-workflow-gmp__system-card"
          :class="{ 'c-workflow-gmp__system-card--platform': isPlatformWorkflow }"
        >
          <h3 v-if="fields.systemTitle?.value" class="c-workflow-gmp__system-title">
            {{ fields.systemTitle.value }}
          </h3>

          <div class="c-workflow-gmp__system-layout">
            <strong v-if="fields.systemLabel?.value" class="c-workflow-gmp__system-label">
              {{ fields.systemLabel.value }}
            </strong>

            <div class="c-workflow-gmp__transfer">
              <div
                class="c-workflow-gmp__transfer-visual"
                :aria-label="
                  fields.transferBadge?.value || fields.transferTitle?.value || 'Transfer Plasmid'
                "
                role="img"
              >
                <span v-if="fields.transferTitle?.value" class="c-workflow-gmp__transfer-label">
                  {{ fields.transferTitle.value }}
                </span>
              </div>
            </div>
            <strong v-if="fields.geneText?.value" class="c-workflow-gmp__gene">
              {{ fields.geneText.value }}
            </strong>

            <div v-if="helperItems.length" class="c-workflow-gmp__helpers">
              <span
                v-for="item in helperItems"
                :key="item.id || item.fields.title?.value"
                class="c-workflow-gmp__plasmid"
                :class="getHelperClass(item)"
                :style="getHelperStyle(item)"
              >
                <span class="c-workflow-gmp__plasmid-flag" aria-hidden="true"></span>
                <span class="c-workflow-gmp__plasmid-text" v-html="item.fields.title.value"></span>
              </span>
            </div>

            <div class="c-workflow-gmp__bracket" aria-hidden="true">
              <span class="c-workflow-gmp__bracket-notch"></span>
            </div>

            <div
              v-if="
                !isPlatformWorkflow &&
                (fields.cellLineText?.value || getImageSrc(fields.cellLineImage))
              "
              class="c-workflow-gmp__cells"
            >
              <div class="c-workflow-gmp__cell-text" v-html="fields.cellLineText?.value"></div>
              <img
                v-if="getImageSrc(fields.cellLineImage)"
                class="c-workflow-gmp__cell-image"
                :src="getImageSrc(fields.cellLineImage)"
                :alt="getImageAlt(fields.cellLineImage, fields.cellLineText?.value || '')"
                loading="lazy"
              />
            </div>

            <div
              v-if="fields.vectorLabel?.value || getImageSrc(fields.vectorImage)"
              class="c-workflow-gmp__vector"
            >
              <img
                v-if="getImageSrc(fields.vectorImage)"
                class="c-workflow-gmp__vector-image"
                :src="getImageSrc(fields.vectorImage)"
                :alt="getImageAlt(fields.vectorImage, fields.vectorLabel?.value || '')"
                loading="lazy"
              />
              <div class="c-workflow-gmp__vector-label" v-html="fields.vectorLabel?.value"></div>
            </div>
          </div>
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type {
  WorkflowGMPGradeStageFields,
  WorkflowGMPHelperItemFields,
  WorkflowGMPPlatformRowFields,
  WorkflowGMPPlatformStepFields,
  WorkflowGMPProps,
  WorkflowGMPTimelineItemFields,
} from '@/types/components/WorkflowGMP';
import type { ImageField, ScItem } from '@/types/sitecore';
import utils from '@/utils';

type WorkflowGMPTimelineItem = ScItem<WorkflowGMPTimelineItemFields> & {
  fields: WorkflowGMPTimelineItemFields;
};
type WorkflowGMPGradeStage = ScItem<WorkflowGMPGradeStageFields> & {
  fields: WorkflowGMPGradeStageFields;
};
type WorkflowGMPHelperItem = ScItem<WorkflowGMPHelperItemFields> & {
  fields: WorkflowGMPHelperItemFields;
};
type WorkflowGMPPlatformRow = ScItem<WorkflowGMPPlatformRowFields> & {
  fields: WorkflowGMPPlatformRowFields;
};
type WorkflowGMPPlatformStep = ScItem<WorkflowGMPPlatformStepFields> & {
  fields: WorkflowGMPPlatformStepFields;
};

const props = defineProps<WorkflowGMPProps>();
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-workflow-gmp'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
const isTimeline = computed(() => {
  const value = fields.value?.IsTimeline?.value;
  return value === undefined ? true : value === true || value === 'true' || value === '1';
});
const isPlatformWorkflow = computed(() => !isTimeline.value);

const platformWorkflowRows = computed<WorkflowGMPPlatformRow[]>(() => {
  const rows = utils.sc.getFieldObj(fields.value, 'platformRows');
  return Array.isArray(rows)
    ? rows.filter((row): row is WorkflowGMPPlatformRow => Boolean(row?.fields?.label?.value))
    : [];
});

const timelineItems = computed<WorkflowGMPTimelineItem[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'timelineItems');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowGMPTimelineItem => Boolean(item?.fields?.label?.value))
    : [];
});

const gradeStages = computed<WorkflowGMPGradeStage[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'gradeStages');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowGMPGradeStage => Boolean(item?.fields?.text?.value))
    : [];
});

const helperItems = computed<WorkflowGMPHelperItem[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'helperItems');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowGMPHelperItem => Boolean(item?.fields?.title?.value))
    : [];
});

const gradeTrackCount = computed(() =>
  gradeStages.value.reduce((max, stage) => {
    const gridStart = Number(stage.fields.gridStart?.value ?? 0);
    const gridSpan = Number(stage.fields.gridSpan?.value ?? 1);

    return Number.isFinite(gridStart) && gridStart > 0 && Number.isFinite(gridSpan)
      ? Math.max(max, gridStart + Math.max(1, gridSpan) - 1)
      : max;
  }, 1)
);

const timelineStyle = computed(() => ({
  '--workflow-gmp-count': `${Math.max(timelineItems.value.length, 1)}`,
  '--workflow-gmp-grade-count': `${Math.max(gradeTrackCount.value, 1)}`,
}));

const hasSystemContent = computed(() => {
  if (isPlatformWorkflow.value) {
    return (
      fields.value?.systemTitle?.value ||
      fields.value?.vectorLabel?.value ||
      getImageSrc(fields.value?.vectorImage)
    );
  }

  return (
    fields.value?.systemTitle?.value ||
    fields.value?.systemLabel?.value ||
    fields.value?.transferTitle?.value ||
    helperItems.value.length ||
    fields.value?.cellLineText?.value ||
    fields.value?.vectorLabel?.value
  );
});

const COLOR_VALUE_PATTERN = /^(#|rgb\(|rgba\(|hsl\(|hsla\(|var\()/i;

const getColorValue = (value?: string) => {
  const color = value?.trim();
  return color && COLOR_VALUE_PATTERN.test(color) ? color : '';
};

const getImageValue = (image?: ImageField) => image?.value || {};
const getImageSrc = (image?: ImageField) => getImageValue(image).src || '';
const getImageAlt = (image: ImageField | undefined, fallback: string) =>
  getImageValue(image).alt || fallback;

const getDotStyle = (item: WorkflowGMPTimelineItem) => {
  const color = getColorValue(item.fields.dotColor?.value);
  return color ? { '--dot-bg': color } : {};
};

const getPlatformSteps = (row: WorkflowGMPPlatformRow) => {
  const steps = utils.sc.getFieldObj(row.fields, 'steps');
  return Array.isArray(steps)
    ? steps.filter((step): step is WorkflowGMPPlatformStep => Boolean(step?.fields?.text?.value))
    : [];
};

const PLATFORM_ROW_VARIANTS = ['scope', 'practice', 'highlight'];

const getPlatformVariant = (index: number) => PLATFORM_ROW_VARIANTS[index] || 'practice';

const getPlatformStepStyle = (step: WorkflowGMPPlatformStep) => ({
  '--platform-step-span': String(toPositiveNumber(step.fields.span?.value, 1)),
});

const toPositiveNumber = (value: number | string | undefined, fallback: number) => {
  const numericValue = Number(value ?? fallback);
  return Number.isFinite(numericValue) ? Math.max(1, numericValue) : fallback;
};

const getGradeStyle = (stage: WorkflowGMPGradeStage) => {
  const gridStart = Number(stage.fields.gridStart?.value ?? 0);
  const gridSpan = toPositiveNumber(stage.fields.gridSpan?.value, 1);

  if (!Number.isFinite(gridStart) || gridStart <= 0) {
    return {};
  }

  return {
    '--grade-start': `${gridStart}`,
    '--grade-span': `${gridSpan}`,
  };
};

const getHelperStyle = (item: WorkflowGMPHelperItem) => {
  const color = getColorValue(item.fields.tone?.value);
  if (!color) return {};

  return {
    '--plasmid-color': color,
    '--plasmid-flag': color.toLowerCase() === '#eeeeee' ? '#3d3d3f' : '#6d9272',
  };
};

const getHelperClass = (item: WorkflowGMPHelperItem) => {
  const tone = item.fields.tone?.value?.trim().toLowerCase() || 'green';
  return getColorValue(tone) ? '' : 'c-workflow-gmp__plasmid--' + tone;
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-workflow-gmp {
  padding: 42px 0 46px;
  background: #fff;
  color: #3d3d3f;

  &-title {
    text-align: center;
  }

  &__inner {
    width: 100%;
  }

  &__title {
    margin: 0;
    color: #3d3d3f;
    font-size: 24px;
    line-height: 1.18;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0;
  }

  &__platform {
    display: grid;
    gap: 10px;
    margin-top: 26px;
  }

  &__platform-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
  }

  &__platform-label {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 34px;
    padding: 7px 10px;
    background: #eeeeee;
    color: #1f2933;
    font-size: 12px;
    line-height: 1.15;
    font-weight: 700;
    text-align: center;
    border-radius: 6px;
  }

  &__platform-track {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
  }

  &__platform-step {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 34px;
    padding: 7px 22px 7px 14px;
    background: #c7d3c8;
    color: #1f2933;
    font-size: 12px;
    line-height: 1.12;
    font-weight: 700;
    text-align: center;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: -18px;
      width: 18px;
      height: 100%;
      background: inherit;
      clip-path: polygon(0 0, 100% 50%, 0 100%);
      z-index: 2;
    }

    span {
      position: relative;
      z-index: 3;
    }
  }

  &__platform-row--scope {
    .c-workflow-gmp__platform-label,
    .c-workflow-gmp__platform-step {
      background: #eeeeee;
      color: #1f2933;
    }

    .c-workflow-gmp__platform-label {
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }

  &__platform-row--practice {
    .c-workflow-gmp__platform-label,
    .c-workflow-gmp__platform-step {
      background: #c7d3c8;
      color: #1f2933;
    }
  }

  &__platform-row--highlight {
    .c-workflow-gmp__platform-label,
    .c-workflow-gmp__platform-step {
      background: #668f6d;
      color: #fff;
    }

    .c-workflow-gmp__platform-track {
      padding: 7px 8px;
      border: 1px dashed #e6c99d;
      border-radius: 6px;
    }
  }

  &__timeline {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    gap: 18px;
    margin-top: 26px;
  }

  &__timeline-line {
    position: absolute;
    top: 76px;
    bottom: 24px;
    left: 22px;
    width: 2px;
    background: #e1e6ea;
    z-index: 0;
  }

  &__timeline-item {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 46px 56px 1fr;
    gap: 12px;
    align-items: center;
  }

  &__stage-label {
    grid-column: 3;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-width: 94px;
    min-height: 28px;
    padding: 6px 16px;
    border-radius: 2px;
    background: #eeeeee;
    color: #3d3d3f;
    font-size: 14px;
    line-height: 1;
    font-weight: 400;
  }

  &__stage-icon {
    grid-column: 2;
    width: 52px;
    height: 52px;
    object-fit: contain;
  }

  &__dot {
    grid-column: 1;
    grid-row: 1;
    justify-self: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--dot-bg, #bccbbe);
  }

  &__grades {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    margin-top: 26px;
  }

  &__grade {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    padding: 9px 20px 9px 26px;
    background: #cbd8cc;
    color: #111827;
    font-size: 15px;
    line-height: 1.18;
    font-weight: 700;
    text-align: center;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: -30px;
      width: 30px;
      height: 100%;
      background: inherit;
      clip-path: polygon(0 0, 100% 50%, 0 100%);
      pointer-events: none;
    }
  }

  &__system-card {
    margin-top: 32px;
    padding: 20px 18px 24px;
    border: 1px solid #e5e8e6;
    border-radius: 12px;
    background: #fff;
  }

  &__system-title {
    margin: 0;
    color: #3d3d3f;
    font-size: 18px;
    line-height: 1.2;
    font-weight: 700;
  }

  &__system-layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 22px;
  }

  &__system-label,
  &__gene,
  &__cell-text,
  &__vector-label {
    color: #3d3d3f;
    font-size: 16px;
    font-weight: 700;
  }

  &__transfer,
  &__helpers,
  &__cells,
  &__vector {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__helpers {
    flex-wrap: wrap;
    gap: 18px;
  }

  &__plasmid {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 104px;
    height: 104px;
    border: 14px solid var(--plasmid-color, #c9d7ca);
    border-radius: 50%;
    background: #fff;
    color: var(--plasmid-flag, #6d9272);
    text-align: center;

    &::before {
      content: '';
      position: absolute;
      top: -15px;
      left: 50%;
      width: 4px;
      height: 30px;
      background: #fff;
      transform: translateX(-2%);
      z-index: 1;
    }
  }

  &__transfer-visual {
    position: relative;
    display: block;
    width: min(100%, 152px);
    aspect-ratio: 256 / 250;
    background: url('../../assets/images/workflowgmp-bg.png') center / contain no-repeat;
  }
  &__transfer-label {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 76px;
    color: #3d3d3f;
    font-size: 13px;
    line-height: 1.2;
    font-weight: 700;
    text-align: center;
    transform: translate(-50%, -50%);
  }
  &__plasmid--gray {
    --plasmid-color: #eeeeee;
    color: #3d3d3f;
  }

  &__plasmid-flag {
    position: absolute;
    top: -17px;
    left: calc(50% + 2px);
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 12px solid currentColor;
    transform: translateX(-6%);
    z-index: 2;
  }

  &__plasmid-text {
    max-width: 72px;
    font-size: 12px;
    line-height: 1.15;
    font-weight: 700;
  }
  &__bracket {
    display: none;
  }

  &__cells {
    gap: 18px;
    justify-content: flex-start;
  }

  &__cell-image {
    width: 120px;
    height: 80px;
    object-fit: contain;
  }

  &__vector {
    gap: 18px;
  }

  &__vector-image {
    width: 96px;
    height: 96px;
    object-fit: contain;
  }
  &__grade,
  &__plasmid-text,
  &__cell-text,
  &__vector-label {
    :where(p) {
      margin: 0;
    }
  }

  &__system-card--platform {
    .c-workflow-gmp__vector {
      justify-content: center;
    }
  }

  @include tablet {
    &__transfer-visual {
      position: relative;
      display: block;
      width: min(100%, 176px);
    }
    &__transfer-label {
      width: 88px;
      font-size: 16px;
    }
    padding: 50px 0 56px;

    &__title {
      font-size: 30px;
    }

    &__platform {
      gap: 6px;
      margin: 30px auto 0;
    }

    &__platform-row {
      grid-template-columns: 190px minmax(0, 1fr);
      gap: 10px;
      align-items: stretch;
    }

    &__platform-label {
      min-height: 40px;
      padding: 7px 8px;
      font-size: 13px;
    }

    &__platform-track {
      display: flex;
      gap: 0;
      min-width: 0;
    }

    &__platform-step {
      flex: var(--platform-step-span, 1) 1 0;
      min-width: 0;
      min-height: 42px;
      padding: 7px 22px 7px 12px;
      font-size: 12px;
      clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%);

      &:not(:first-child) {
        padding-left: 28px;
        clip-path: polygon(
          0 0,
          calc(100% - 20px) 0,
          100% 50%,
          calc(100% - 20px) 100%,
          0 100%,
          20px 50%
        );
      }

      &::after {
        display: none;
      }
    }

    &__platform-row--highlight {
      .c-workflow-gmp__platform-track {
        gap: 0;
        padding: 6px 8px 6px 138px;
      }
    }

    &__timeline {
      grid-template-columns: repeat(var(--workflow-gmp-count), minmax(0, 1fr));
      gap: 0;
      margin-top: 26px;
    }

    &__timeline-line {
      top: 166px;
      right: -10px;
      bottom: auto;
      left: -10px;
      width: auto;
      height: 2px;
    }

    &__timeline-item {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 28px 100px 28px;
      justify-items: center;
      gap: 12px;
      min-height: 184px;
    }

    &__stage-label,
    &__stage-icon,
    &__dot {
      grid-column: 1;
    }

    &__stage-label {
      min-width: 90px;
      font-size: 13px;
    }

    &__stage-icon {
      grid-row: 2;
      align-self: center;
      width: 62px;
      height: 62px;
    }

    &__dot {
      grid-row: 3;
      width: 28px;
      height: 28px;
    }

    &__grades {
      grid-template-columns: repeat(var(--workflow-gmp-grade-count), minmax(0, 1fr));
      gap: 0;
      width: 100%;
      margin: 18px auto 0;
    }

    &__grade {
      grid-column: var(--grade-start, auto) / span var(--grade-span, 1);
      min-height: 42px;
      padding: 8px 28px 8px 18px;
      font-size: 14px;
      clip-path: polygon(0 0, calc(100% - 28px) 0, 100% 50%, calc(100% - 28px) 100%, 0 100%);

      &:not(:first-child) {
        padding-left: 34px;
        clip-path: polygon(
          0 0,
          calc(100% - 28px) 0,
          100% 50%,
          calc(100% - 28px) 100%,
          0 100%,
          28px 50%
        );
      }

      &::after {
        display: none;
      }
    }

    &__system-card {
      margin-top: 34px;
      padding: 24px 44px 34px;
      border-radius: 12px;
    }

    &__system-title {
      font-size: 18px;
    }

    &__system-layout {
      grid-template-areas:
        'label transfer gene helpers'
        '. bracket bracket bracket'
        '. cells cells vector';
      grid-template-columns: 120px 190px minmax(130px, 1fr) minmax(280px, 1.4fr);
      gap: 14px 22px;
      align-items: center;
      margin-top: 22px;
    }

    &__system-label {
      grid-area: label;
    }

    &__transfer {
      grid-area: transfer;
    }

    &__gene {
      grid-area: gene;
    }

    &__helpers {
      grid-area: helpers;
      justify-content: space-between;
      gap: 16px;
    }

    &__bracket {
      position: relative;
      display: block;
      grid-area: bracket;
      height: 46px;
      margin: -4px 18px 0;
      border-bottom: 2px solid #d8c59e;
      color: #d8c59e;

      &::before,
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        width: 2px;
        height: 24px;
        background: currentColor;
      }

      &::before {
        left: 0;
        transform: rotate(-6deg);
        transform-origin: bottom;
      }

      &::after {
        right: 0;
        transform: rotate(6deg);
        transform-origin: bottom;
      }
    }

    &__bracket-notch {
      position: absolute;
      left: 50%;
      bottom: -11px;
      width: 23px;
      height: 15px;
      background: #fff;
      transform: translateX(-50%);

      &::before,
      &::after {
        content: '';
        position: absolute;
        top: 19px;
        width: 20px;
        height: 2px;
        background: currentColor;
      }

      &::before {
        left: -8px;
        transform: rotate(54deg);
        transform-origin: right center;
      }

      &::after {
        right: -8px;
        transform: rotate(-54deg);
        transform-origin: left center;
      }
    }

    &__cells {
      grid-area: cells;
      justify-content: center;
      gap: 22px;
    }

    &__vector {
      grid-area: vector;
      justify-content: center;
      gap: 18px;
    }

    &__system-card--platform {
      .c-workflow-gmp__system-layout {
        grid-template-areas:
          'label transfer gene helpers'
          '. bracket bracket bracket'
          '. vector vector vector';
      }

      .c-workflow-gmp__vector {
        justify-self: center;
        width: 100%;
        max-width: 360px;
      }
    }
  }
  &__transfer-label {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 76px;
    color: #3d3d3f;
    font-size: 13px;
    line-height: 1.2;
    font-weight: 700;
    text-align: center;
    transform: translate(-50%, -50%);
  }

  @include tablet-landscape {
    padding: 66px 0 70px;

    &__title {
      font-size: 31px;
    }

    &__platform {
      margin-top: 30px;
    }

    &__platform-row {
      grid-template-columns: 200px minmax(0, 1fr);
      gap: 12px;
    }

    &__platform-label {
      min-height: 42px;
      font-size: 13px;
    }

    &__platform-track {
      gap: 0;
    }

    &__platform-step {
      min-height: 42px;
      font-size: 12px;
    }

    &__platform-row--highlight {
      .c-workflow-gmp__platform-track {
        gap: 0;
        padding-left: 158px;
      }
    }

    &__timeline {
      margin-top: 28px;
    }

    &__system-card {
      margin-right: 10px;
      margin-left: 10px;
      padding: 24px 44px 36px;
    }

    &__system-layout {
      grid-template-columns: 124px 194px 1fr 390px;
      gap: 16px 26px;
    }

    &__system-card--platform {
      .c-workflow-gmp__system-layout {
        grid-template-columns: 124px 194px 1fr 390px;
      }
    }
  }
}
</style>
